import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { listProducts } from '../actions/productActions';
import { fetchCategories } from '../actions/categoryActions';
import ProductCard from '../components/products/ProductCard';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';

const HomePage = () => {
  const dispatch = useDispatch();
  const productList = useSelector(state => state.productList);
  const { loading, error, products } = productList;
  
  const categoryList = useSelector(state => state.categoryList);
  const { categories } = categoryList;
  
  // 添加视频静音状态、音量和引用
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.5); // 默认音量50%
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const videoRef = useRef(null);
  
  // 切换静音/取消静音的函数
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  // 调整音量的函数
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      
      // 如果音量设为0，则自动静音
      if (newVolume === 0) {
        videoRef.current.muted = true;
        setIsMuted(true);
      } else if (isMuted) {
        // 如果之前是静音，但现在调整了音量，则取消静音
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    }
  };
  
  // 用于加载产品和分类的Effect
  useEffect(() => {
    dispatch(listProducts());
    dispatch(fetchCategories());
  }, [dispatch]);
  
  // 单独的Effect用于处理视频音量
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);
  
  // Get featured products (limit to 8)
  const featuredProducts = products ? products.slice(0, 8) : [];
  
  return (
    <div className="home-page">
      {/* Hero Banner */}
      <div className="relative mb-16 rounded-xl overflow-hidden shadow-xl">
        <div className="aspect-w-16 aspect-h-9">
          <video 
            ref={videoRef}
            className="w-full h-full object-cover" 
            autoPlay 
            loop 
            muted={isMuted}
            playsInline
          >
            <source src="/videos/wly.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="container mx-auto text-center text-white px-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">南昌市玉洒贸易有限公司</h1>
              <div className="w-16 sm:w-20 h-1 bg-white mx-auto mb-4 md:mb-6"></div>
              <p className="text-xl sm:text-2xl md:text-3xl mb-4 sm:mb-8">五粮液官方授权店</p>
              <p className="text-base sm:text-lg mb-6 sm:mb-10 max-w-2xl mx-auto">正品保障，优质服务，尊享品质生活</p>
              <Link
                to="/products"
                className="bg-white text-red-800 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-gray-100 transition duration-300 shadow-lg"
              >
                浏览产品
              </Link>
            </div>
          </div>
          
          {/* 添加声音控制区域 - 放在右下角，避免被文字遮挡 */}
          <div className="absolute bottom-6 right-6 z-20 flex items-center space-x-2">
            {/* 如果显示音量滑块，则渲染滑块组件 */}
            {showVolumeSlider && (
              <div className="bg-black bg-opacity-50 px-3 py-2 rounded-full flex items-center">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 accent-white"
                  title="调节音量"
                />
              </div>
            )}
            
            {/* 音量图标按钮 */}
            <button 
              onClick={() => setShowVolumeSlider(!showVolumeSlider)}
              className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-300 focus:outline-none shadow-lg"
              aria-label="调节音量"
              title="调节音量"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            </button>
            
            {/* 静音/取消静音按钮 */}
            <button 
              onClick={toggleMute}
              className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-300 focus:outline-none shadow-lg"
              aria-label={isMuted ? "取消静音" : "静音"}
              title={isMuted ? "取消静音" : "静音"}
            >
              {isMuted ? (
                // 静音图标
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                // 有声图标
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Brand Story */}
      <div className="mb-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">品牌故事</h2>
            <div className="w-16 h-1 bg-red-700 mx-auto"></div>
          </div>
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <div className="relative">
                <img
                  src="/images/front.png"
                  alt="五粮液品牌故事"
                  className="rounded-xl w-full shadow-2xl"
                />
              </div>
            </div>
            <div className="md:w-1/2">
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                五粮液是中国浓香型白酒的代表，以五种粮食为原料，精选高粱、大米、糯米、小麦、玉米五谷酿制而成。其酿造工艺历史悠久，可追溯至明朝年间。
              </p>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                南昌市玉洒贸易有限公司作为五粮液的官方授权经销商，致力于为消费者提供正品五粮液产品，让更多人品尝到正宗的五粮液美酒。
              </p>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                我们秉承"品质第一，顾客至上"的服务理念，为您提供最优质的产品和最贴心的服务。
              </p>
              <Link
                to="/about"
                className="text-red-700 font-bold text-lg border-b-2 border-red-700 hover:border-red-800 transition-colors pb-1"
              >
                了解更多 &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Product Categories */}
      <div className="mb-24 py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">热门分类</h2>
            <div className="w-16 h-1 bg-red-700 mx-auto"></div>
          </div>
          
          {categoryList.loading ? (
            <Loader />
          ) : categoryList.error ? (
            <Message variant="error">{categoryList.error}</Message>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* 只显示前4个分类 */}
              {categories.slice(0, 4).map(category => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="h-48 bg-gray-300 relative overflow-hidden">
                    {category.image_url && (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300"></div>
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-2xl font-bold mb-2 group-hover:text-red-700 transition-colors">{category.name}</h3>
                    {category.description && (
                      <p className="text-gray-600">{category.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          {/* 添加查看所有分类的链接 */}
          {categories && categories.length > 4 && (
            <div className="text-center mt-8">
              <Link
                to="/products"
                className="text-red-700 font-medium hover:text-red-800 inline-flex items-center"
              >
                查看所有分类
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
      
      {/* Featured Products */}
      <div className="mb-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">精选产品</h2>
            <div className="w-16 h-1 bg-red-700 mx-auto"></div>
          </div>
          
          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant="error">{error}</Message>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {featuredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              
              <div className="text-center mt-12">
                <Link
                  to="/products"
                  className="bg-red-700 text-white px-8 py-3 rounded-full hover:bg-red-800 inline-block font-semibold transition duration-300 shadow-md"
                >
                  查看全部产品
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="relative bg-red-800 text-white py-16 px-4 rounded-xl overflow-hidden mb-12">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900 to-red-800 opacity-90"></div>
        <div className="absolute inset-0 opacity-20" style={{ 
          backgroundImage: 'url(/images/pattern.png)', 
          backgroundRepeat: 'repeat',
          backgroundSize: '300px'
        }}></div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">开始享受优质的五粮液产品</h2>
          <p className="text-xl mb-8">南昌市玉洒贸易有限公司为您提供最正宗的五粮液体验</p>
          <Link
            to="/products"
            className="bg-white text-red-800 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition duration-300 shadow-lg"
          >
            立即购买
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
